import { RECIPE_INFO } from './recipe-info';
const REEF_SOLUTIONS_BASE = {
    // Foundational Elements
    alk_bicarb: {
        kind: 'parameter',
        slug: 'alk-bicarb',
        name: 'Alkalinity: Sodium Bicarbonate (NaHCO3)',
        category: 'Foundational Elements',
        what_it_is: "A high-purity, food-grade chemical compound, commonly known as baking soda. In the aquarium context, it is used as a straightforward and cost-effective source of bicarbonate ions, which are a primary component of alkalinity.",
        what_it_does: 'Directly adds bicarbonate ions (HCO3-) to the water, increasing its buffering capacity (alkalinity). This has a neutral or slightly depressing effect on system pH, making it a suitable choice for aquariums that naturally run a high pH or when a pH spike is undesirable.',
        recipe: {
            amounts: { '100': '7.85g', '250': '19.62g', '500': '39.23g', '1000': '78.47g', '3785': '297g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 80% of the final volume of RO/DI water to a container.</li>
                <li>Dissolve <strong>{amount}</strong> of Sodium Bicarbonate (NaHCO3) into the water.</li>
                <li>Once fully dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the alkalinity in 100 L of water by approximately 0.026 dKH.',
        units: 'dKH',
        ref_vol_L: 100,
        raise: 0.026,
    },
    alk_carbonate: {
        kind: 'parameter',
        slug: 'alk-carbonate',
        name: 'Alkalinity: Sodium Carbonate (Na2CO3)',
        category: 'Foundational Elements',
        what_it_is: "A chemical compound, commonly known as soda ash, which can be created by baking standard Sodium Bicarbonate (baking soda). This process drives off water and carbon dioxide, converting the bicarbonate into the more potent carbonate form.",
        what_it_does: 'Provides alkalinity in the form of carbonate ions (CO3--). When added to the aquarium, it reacts with carbonic acid in the water, raising pH more significantly than sodium bicarbonate. Per mole, it provides twice the alkalinity, making it a powerful tool for maintaining both alkalinity and pH in systems that tend toward acidity.',
        recipe: {
            amounts: { '100': '9.91g', '250': '24.77g', '500': '49.54g', '1000': '99.08g', '3785': '375g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>First, create Sodium Carbonate by spreading Sodium Bicarbonate on a baking sheet and bake at 200°C (400°F) for at least one hour. To yield the target amount of Sodium Carbonate, you will need to start with approximately 1.6x that amount of Sodium Bicarbonate (e.g., to get 375g of Soda Ash, start with ~594g of Baking Soda). Let cool completely.</li>
                <li>Dissolve the <strong>{amount}</strong> of Sodium Carbonate (Na2CO3) into approximately 80% of the final volume of warm RO/DI water.</li>
                <li>Once dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the alkalinity in 100 L of water by approximately 0.052 dKH.',
        units: 'dKH',
        ref_vol_L: 100,
        raise: 0.052,
    },
    alk_hydroxide: {
        kind: 'parameter',
        slug: 'alk-hydroxide',
        name: 'Alkalinity: Sodium Hydroxide (NaOH)',
        category: 'Foundational Elements',
        safety: '<strong>SAFETY WARNING:</strong> NaOH is highly caustic. Always wear chemical-resistant gloves and safety goggles. Add NaOH slowly to water, never the other way around, as the reaction is highly exothermic and can cause boiling.',
        what_it_is: 'A highly caustic inorganic compound, also known as lye. It is the most potent chemical option for simultaneously raising both alkalinity and pH. Due to its hazardous nature, it should only be handled by experienced aquarists with appropriate safety precautions.',
        what_it_does: 'Rapidly and efficiently consumes carbonic acid in the water, resulting in a significant and immediate increase in both pH and alkalinity. It is particularly effective in heavily stocked systems or those with CO2 reactors that consistently depress pH.',
        recipe: {
            amounts: { '100': '7.48g', '250': '18.69g', '500': '37.38g', '1000': '74.77g', '3785': '283g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Fill a suitable container with approximately 80% of the final volume of cool RO/DI water.</li>
                <li>Slowly and carefully add <strong>{amount}</strong> of Sodium Hydroxide (NaOH) to the water. The container will get very hot; allow it to cool.</li>
                <li>Once cooled and dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the alkalinity in 100 L of water by approximately 0.052 dKH.',
        units: 'dKH',
        ref_vol_L: 100,
        raise: 0.052,
    },
    calcium: {
        kind: 'parameter',
        slug: 'calcium',
        name: 'Calcium: Calcium Chloride Dihydrate (CaCl2·2H2O)',
        category: 'Foundational Elements',
        what_it_is: 'A hydrated salt of calcium and chlorine that is highly soluble in water. It is the industry standard for DIY calcium supplementation due to its purity, potency, and cost-effectiveness. Using food or pharmaceutical grade material is strongly recommended to avoid contaminants like bromide.',
        what_it_does: 'Directly provides calcium ions (Ca2+), which are a fundamental building block of the aragonite skeletons of stony corals, clams, and the structure of coralline algae. Maintaining stable calcium levels is critical for coral growth, health, and resilience.',
        recipe: {
            amounts: { '100': '13.21g', '250': '33.02g', '500': '66.05g', '1000': '132.10g', '3785': '500g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 80% of the final volume of RO/DI water to a container.</li>
                <li>Dissolve <strong>{amount}</strong> of Calcium Chloride Dihydrate (CaCl2·2H2O) into the water.</li>
                <li>Once dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Calcium level in 100 L of water by approximately 0.36 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.36,
    },
    magnesium: {
        kind: 'parameter',
        slug: 'magnesium',
        name: 'Magnesium: Balanced Supplement (Blend)',
        category: 'Foundational Elements',
        what_it_is: 'A precisely formulated supplement using a blend of two hydrated magnesium salts: Magnesium Chloride Hexahydrate (MgCl2·6H2O) and Magnesium Sulfate Heptahydrate (MgSO4·7H2O).',
        what_it_does: 'Maintains the proper concentration of magnesium, which plays a crucial role in preventing the spontaneous, non-biological precipitation of calcium carbonate. This keeps calcium and alkalinity ions separate and bioavailable for corals. The blended recipe maintains the natural seawater ratio of chloride to sulfate, preventing ionic imbalance over time.',
        recipe: {
            amounts: {
                '3785': '5.25 cups (~933g) of MgCl2·6H2O and 2 cups (~332g) of MgSO4·7H2O',
            },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 2 L of RO/DI water to a 1-gallon jug.</li>
                <li>Add <strong>{amount}</strong>.</li>
                <li>Shake well to dissolve, then add RO/DI water to a final total volume of 1 gallon (3.785 L).</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Magnesium level in 100 L of water by approximately 0.38 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.38,
    },
    rhf_alk: {
        kind: 'parameter',
        slug: 'rhf-alk',
        name: "RHF Alkalinity (Part 1)",
        category: 'Foundational Elements',
        what_it_is: "Randy Holmes-Farley's widely respected DIY 2-part dosing system (Recipe #1). It uses baked baking soda (Sodium Carbonate) for a potent and pH-boosting effect.",
        what_it_does: "Provides the carbonate alkalinity necessary to buffer system pH against daily swings and supplies the carbonate ions corals need to build their aragonite skeletons. As 'Part 1' of a balanced system, it is designed to be consumed by corals at a similar rate to the 'Part 2' calcium component.",
        recipe: {
            amounts: { '100': '9.91g', '250': '24.77g', '500': '49.54g', '1000': '99.08g', '3785': '375g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>First, create Sodium Carbonate by spreading Sodium Bicarbonate on a baking sheet and bake at 200°C (400°F) for at least one hour. To yield the target amount of Sodium Carbonate, you will need to start with approximately 1.6x that amount of Sodium Bicarbonate (e.g., to get 375g of Soda Ash, start with ~594g of Baking Soda). Let cool completely.</li>
                <li>Dissolve the <strong>{amount}</strong> of Sodium Carbonate (Na2CO3) into approximately 80% of the final volume of warm RO/DI water.</li>
                <li>Once dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the alkalinity in 100 L of water by approximately 0.052 dKH.',
        units: 'dKH',
        ref_vol_L: 100,
        raise: 0.052,
    },
    rhf_ca: {
        kind: 'parameter',
        slug: 'rhf-ca',
        name: "RHF Calcium (Part 2)",
        category: 'Foundational Elements',
        what_it_is: "Randy Holmes-Farley's DIY 2-part dosing system. It uses Calcium Chloride Dihydrate, the industry standard for calcium supplementation.",
        what_it_does: 'Provides the elemental calcium required for coral skeletal growth, designed to ionically balance the alkalinity dosed from Part 1. Regular, balanced dosing of both parts ensures that the foundational elements for coral growth are consistently available.',
        recipe: {
            amounts: { '100': '13.21g', '250': '33.02g', '500': '66.05g', '1000': '132.10g', '3785': '500g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 80% of the final volume of RO/DI water to a container.</li>
                <li>Dissolve <strong>{amount}</strong> of Calcium Chloride Dihydrate (CaCl2·2H2O) into the water. The solution may get hot.</li>
                <li>Once dissolved, add RO/DI water to the desired final total volume.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Calcium level in 100 L of water by approximately 0.36 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.36,
    },
    rhf_mg: {
        kind: 'parameter',
        slug: 'rhf-mg',
        name: 'RHF Magnesium',
        category: 'Foundational Elements',
        what_it_is: "Randy Holmes-Farley's DIY magnesium supplement recipe, which uses a specific, ionically balanced blend of Magnesium Chloride and Magnesium Sulfate salts.",
        what_it_does: 'Maintains stable magnesium levels, which is critical for preventing the abiotic (non-biological) precipitation of calcium and alkalinity. This ensures these crucial elements remain dissolved and bioavailable for uptake by corals and other organisms.',
        recipe: {
            amounts: {
                '3785': '5 cups (~888g) MgCl2·6H2O and 3 cups (~498g) MgSO4·7H2O',
            },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 2 L of RO/DI water to a 1-gallon jug.</li>
                <li>Add <strong>{amount}</strong>.</li>
                <li>Shake well to dissolve, then add RO/DI water to a final total volume of 1 gallon (3.785 L).</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Magnesium level in 100 L of water by approximately 0.41 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.41,
    },
    // Nutrient Control
    lanthanum: {
        kind: 'parameter',
        slug: 'lanthanum',
        name: 'Phosphate Remover: Lanthanum Chloride (LaCl3)',
        category: 'Nutrient Control and Supplementation',
        notes: '<strong>CAUTION:</strong> If the precipitate enters the display tank, it can cause severe respiratory distress in fish.',
        what_it_is: 'A solution containing a rare-earth element, Lanthanum, in the form of Lanthanum Chloride. It acts as a highly effective flocculant, specifically targeting phosphate ions for rapid removal.',
        what_it_does: 'Reacts directly and immediately with orthophosphate ions (PO43–) in the water to form an insoluble precipitate, Lanthanum Phosphate (LaPO4). This solid precipitate must be immediately removed by fine mechanical filtration (e.g., a 1-10 micron filter sock) to prevent it from settling in the tank.',
        recipe: {
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Create a manageable working solution from a concentrate. Mix 5 ml of a commercial-grade LaCl3 concentrate into 1 liter of RO/DI water.</li>
                <li>This diluted solution should be administered via a very slow drip into a high-flow area directly upstream of the filter sock.</li>
            </ol>`,
        },
        dosing_effect: '1 mL of this working solution will remove approximately 0.012 ppm of Phosphate from 100 L of water.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: -0.012,
    },
    ammonium_bicarb: {
        kind: 'parameter',
        slug: 'ammonium-bicarb',
        name: 'Nitrogen: Ammonium Bicarbonate ((NH4)HCO3)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A chemical compound that serves as a bioavailable source of nitrogen (as ammonium) and bicarbonate. It is often used in ultra-low nutrient systems (ULNS) to provide a targeted form of nitrogen.',
        what_it_does: 'Provides ammonium (NH4+), a form of nitrogen that corals can assimilate more energetically efficiently than nitrate. A key advantage of this specific compound is that its consumption by organisms does not deplete alkalinity, as both the ammonium and bicarbonate ions are utilized.',
        recipe: {
            amounts: { '100': '2.00g', '250': '5.00g', '500': '10.00g', '1000': '20.00g', '3785': '75.70g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Ammonium Bicarbonate ((NH4)HCO3) in enough RO/DI water to make a final volume of {volume}.</li>
                <li>The solution should be kept tightly sealed to prevent ammonia off-gassing.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Ammonia (NH3) level in 100 L of water by approximately 0.043 ppm — which fully nitrifies to approximately 0.157 ppm Nitrate (NO3).',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.043,
        nitrifiesToNitrate: true,
    },
    ammonium_chloride: {
        kind: 'parameter',
        slug: 'ammonium-chloride',
        name: 'Nitrogen: Ammonium Chloride (NH4Cl)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A more shelf-stable inorganic compound used for providing a direct source of ammonium for nitrogen supplementation in reef aquariums.',
        what_it_does: 'Provides an efficient source of nitrogen for coral uptake. Unlike the bicarbonate form, the biological process of its consumption (nitrification of ammonium to nitrate) is an acid-producing process that will slowly deplete system alkalinity over time.',
        recipe: {
            amounts: { '100': '1.35g', '250': '3.38g', '500': '6.75g', '1000': '13.50g', '3785': '51.09g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Ammonium Chloride (NH4Cl) in enough RO/DI water to make a final volume of {volume}.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Ammonia (NH3) level in 100 L of water by approximately 0.043 ppm — which fully nitrifies to approximately 0.157 ppm Nitrate (NO3).',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.043,
        nitrifiesToNitrate: true,
    },
    sodium_nitrate: {
        kind: 'parameter',
        slug: 'sodium-nitrate',
        name: 'Nitrogen: Sodium Nitrate (NaNO3)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A high-purity, stable chemical salt used for direct nitrate supplementation. It is a common choice for raising nitrate levels without adding other elements like potassium.',
        what_it_does: 'Directly raises nitrate levels to help achieve a balanced N:P (Nitrogen to Phosphorus) ratio, which can be crucial for coral health and for combating issues like cyanobacteria, especially in systems where direct ammonia dosing is not desired or practical.',
        recipe: {
            amounts: { '100': '4.42g', '250': '11.05g', '500': '22.10g', '1000': '44.20g', '3785': '167.28g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Sodium Nitrate (NaNO3) in enough RO/DI water to make a final volume of {volume}.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Nitrate level in 100 L of water by approximately 0.32 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.32,
    },
    potassium_nitrate: {
        kind: 'parameter',
        slug: 'potassium-nitrate',
        name: 'Nitrogen: Potassium Nitrate (KNO3)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A high-purity chemical compound that provides both nitrate (for nitrogen) and potassium, two essential macronutrients for photosynthetic organisms.',
        what_it_does: 'Raises nitrate levels to balance the N:P ratio while simultaneously supplementing potassium, which is consumed by corals for various metabolic functions and is linked to coloration. This is an efficient way to provide two key elements in one solution.',
        recipe: {
            amounts: { '100': '2.00g', '250': '5.00g', '500': '10.00g', '1000': '20.00g', '3785': '75.70g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Potassium Nitrate (KNO3) in enough RO/DI water to make a final volume of {volume}.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Nitrate level in 100 L of water by approximately 0.12 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.12,
    },
    tsp: {
        kind: 'parameter',
        slug: 'tsp',
        name: 'Phosphate: Trisodium Phosphate (Na3PO4)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A high-purity, highly soluble phosphate salt used for phosphorus supplementation. It is very potent and should be used with precision to avoid overdosing.',
        what_it_does: "Provides a direct source of phosphate, an essential nutrient for all life, critical for cellular energy transfer (ATP) and the formation of DNA/RNA. A complete lack of phosphate ('bottoming out') can lead to coral starvation and outbreaks of pests like dinoflagellates.",
        recipe: {
            amounts: { '100': '0.19g', '250': '0.47g', '500': '0.94g', '1000': '1.88g', '3785': '7.11g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Trisodium Phosphate (Na3PO4) in enough RO/DI water to make a final volume of {volume}.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Phosphate level in 100 L of water by approximately 0.01 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.01,
    },
    mkp: {
        kind: 'parameter',
        slug: 'mkp',
        name: 'Phosphate: Monopotassium Phosphate (KH2PO4)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: "A high-purity phosphate salt that also supplements potassium. It's a common and stable choice for phosphate dosing in both reef and freshwater planted aquariums.",
        what_it_does: 'Provides essential phosphate for coral biological functions while also contributing to potassium levels. This is a very efficient way to supplement two important elements, particularly if potassium levels are also being depleted.',
        recipe: {
            amounts: { '100': '1.50g', '250': '3.75g', '500': '7.50g', '1000': '15.00g', '3785': '56.78g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Dissolve <strong>{amount}</strong> of Monopotassium Phosphate (KH2PO4) in enough RO/DI water to make a final volume of {volume}.</li>
            </ol>`,
        },
        dosing_effect: '1 mL will raise the Phosphate level in 100 L of water by approximately 0.105 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.105,
    },
    coral_snow: {
        kind: 'ratio',
        slug: 'coral-snow',
        name: 'Coral Snow (Calcium Carbonate)',
        category: 'Nutrient Control and Supplementation',
        what_it_is: 'A DIY flocculant and water clarifier made from a suspension of fine, high-purity Calcium Carbonate powder in RO/DI water. It is not a source of calcium for coral growth, but rather a water treatment.',
        what_it_does: 'The microscopic calcium carbonate particles bind to fine particulate matter, yellowing compounds (gelbstoff), and some dissolved organic compounds in the water column. This clumping action (flocculation) makes these particles larger and easier for a protein skimmer to remove. It can also be used as a liquid carrier to deliver other additives (like amino acids or trace elements) directly to corals.',
        recipe: {
            amounts: { '100': '46g', '250': '115g', '500': '230g', '1000': '460g', '3785': '1741g' },
            template: `<ol class="list-decimal list-inside space-y-2">
                <li>Add approximately 85-90% of the final volume of RO/DI water to a suitable bottle (e.g., for a 500mL solution, use 425-450mL of water).</li>
                <li>Add <strong>{amount}</strong> of Calcium Carbonate powder to the water.</li>
                <li>Shake the bottle vigorously to mix.</li>
                <li>Let the solution sit for two hours. It will then be ready for use.</li>
            </ol>`,
        },
        dosing_effect: 'Recommended dose is 5mL per 50 US gallons of aquarium volume.',
        notes: 'Be sure to shake up the bottle prior to each use as the powder will settle.',
        units: 'mL',
        dose_ratio_ml: 5,
        dose_ratio_gallons: 50,
    },
    // Minor and Trace Elements
    strontium: {
        kind: 'parameter',
        slug: 'strontium',
        name: 'Strontium (SrCl2·6H2O)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Strontium Chloride Hexahydrate, a chemical salt used to supplement the minor element Strontium, which is chemically similar to calcium.',
        what_it_does: 'Strontium is incorporated into coral skeletons alongside calcium, reinforcing the aragonite structure. It is believed to make skeletons stronger, less brittle, and may play a role in biological processes. It is depleted from aquarium water over time.',
        recipe: {
            amounts: { '100': '2.00g', '250': '5.00g', '500': '10.00g', '1000': '20.00g', '3785': '75.70g' },
            template: `Dissolve <strong>{amount}</strong> of Strontium Chloride Hexahydrate (SrCl2·6H2O) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Strontium level in 100 L of water by approximately 0.066 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.066,
    },
    iron: {
        kind: 'parameter',
        slug: 'iron',
        name: 'Iron (Ferrous Gluconate)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Ferrous Gluconate, which is a chelated and readily bioavailable form of iron (Fe2+). Chelation helps keep the iron in a usable form in the high pH environment of a reef tank.',
        what_it_does: 'Iron is a critical cofactor in many enzymes and is essential for chlorophyll production and photosynthesis. It promotes the growth and deep green coloration of macroalgae (like Chaetomorpha) in a refugium, which enhances nutrient export and overall system stability.',
        recipe: {
            amounts: { '100': '8.00g', '250': '20.00g', '500': '40.00g', '1000': '80.00g', '3785': '302.80g' },
            template: `Dissolve <strong>{amount}</strong> of Ferrous Gluconate powder in enough RO/DI water to make a final volume of {volume}. Adding a few mL of vinegar per liter can help maintain bioavailability.`,
        },
        dosing_effect: '1 mL will raise the Iron level in 100 L of water by approximately 0.093 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.093,
        notes: 'Assumes ferrous gluconate dihydrate (C12H22FeO14·2H2O, 482 g/mol).',
    },
    silicate: {
        kind: 'parameter',
        slug: 'silicate',
        name: 'Silicate (Na2SiO3)',
        category: 'Minor and Trace Elements',
        what_it_is: "A diluted solution of Sodium Silicate, a compound often purchased as a concentrated liquid known as 'water glass'.",
        what_it_does: 'Provides dissolved silica, which serves as an essential building block for the skeletons (frustules) of diatoms and the spicules of many sponges. Controlled dosing can promote a beneficial diatom population that outcompetes nuisance pests like dinoflagellates for resources.',
        recipe: {
            template: `To create a more manageable working solution, take 10 mL of a commercial Sodium Silicate liquid concentrate and add it to 990 mL of RO/DI water to make a final volume of 1 L. Mix thoroughly before each use.`,
        },
        dosing_effect: '1 mL will raise the Silicate (SiO2) level in 100 L of water by approximately 0.04 ppm. (This is based on an assumed concentrate strength and may vary).',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.04,
    },
    manganese: {
        kind: 'parameter',
        slug: 'manganese',
        name: 'Manganese (MnCl2·4H2O)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A highly diluted solution of Manganese (II) Chloride Tetrahydrate, a salt used for trace manganese supplementation. A serial dilution is required due to the extremely low target concentration.',
        what_it_does: 'Manganese acts as a vital enzyme cofactor for photosynthesis and is important for coral nutrition and metabolism. It has been linked to enhancing the intensity of red pigmentation in stony corals.',
        recipe: {
            template: `<p>This is a serial dilution recipe:</p><ol class="list-decimal list-inside space-y-2">
                <li><strong>Primary Stock:</strong> Dissolve 1.0 g of Manganese (II) Chloride Tetrahydrate (MnCl2·4H2O) in enough RO/DI water to make a final volume of 1 L.</li>
                <li><strong>Secondary (Working) Stock:</strong> Take 1 mL of the Primary Stock and dilute it with enough RO/DI water to make a final volume of 1 L.</li>
            </ol>`,
        },
        dosing_effect: '1 mL of the secondary stock will raise the Manganese level in 100 L of water by approximately 0.0028 µg/L.',
        units: 'µg/L',
        ref_vol_L: 100,
        raise: 0.0028,
    },
    potassium: {
        kind: 'parameter',
        slug: 'potassium',
        name: 'Potassium (KCl)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Potassium Chloride, a simple salt used to raise potassium levels without significantly impacting other parameters.',
        what_it_does: 'Potassium is a major ion in natural seawater, vital for coral metabolic processes and protein synthesis. It is particularly associated with enhancing pink, red, and purple coloration in SPS corals, and can be depleted quickly in heavily stocked systems.',
        recipe: {
            amounts: { '100': '19.00g', '250': '47.50g', '500': '95.00g', '1000': '190.00g', '3785': '719.15g' },
            template: `Dissolve <strong>{amount}</strong> of Potassium Chloride (KCl) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Potassium level in 100 L of water by approximately 1.0 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 1.0,
    },
    iodide: {
        kind: 'parameter',
        slug: 'iodide',
        name: 'Iodide (KI)',
        category: 'Minor and Trace Elements',
        what_it_is: "A solution of Potassium Iodide, which provides iodide (I-), a safer and more stable form of iodine for aquarium use compared to Lugol's solution (elemental iodine, I2).",
        what_it_does: 'Iodide is utilized by soft corals (especially Xenia and pulsing corals) and macroalgae, is essential for the molting of crustaceans like shrimp, and is believed to act as an antioxidant for corals, helping to neutralize damaging oxygen radicals produced under intense light.',
        recipe: {
            amounts: { '100': '5.00g', '250': '12.50g', '500': '25.00g', '1000': '50.00g', '3785': '189.25g' },
            template: `Dissolve <strong>{amount}</strong> of Potassium Iodide (KI) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Iodide level in 100 L of water by approximately 0.38 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.38,
    },
    bromide: {
        kind: 'parameter',
        slug: 'bromide',
        name: 'Bromide (NaBr)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Sodium Bromide, a salt used to supplement bromide, which is present in natural seawater at approximately 65 ppm.',
        what_it_does: 'Bromide is used by some corals in the synthesis of chromoproteins, the fluorescent proteins responsible for their vibrant coloration. It is particularly linked to enhancing blues and purples. It is often depleted from aquarium water and can be a limiting factor for certain colors.',
        recipe: {
            amounts: { '100': '8.50g', '250': '21.25g', '500': '42.50g', '1000': '85.00g', '3785': '321.73g' },
            template: `Dissolve <strong>{amount}</strong> of Sodium Bromide (NaBr) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Bromide level in 100 L of water by approximately 0.66 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.66,
    },
    fluoride: {
        kind: 'parameter',
        slug: 'fluoride',
        name: 'Fluoride (NaF)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Sodium Fluoride, a salt used for trace fluoride supplementation. It is present in natural seawater and is incorporated into coral skeletons.',
        what_it_does: "Fluoride is incorporated into the coral's aragonite skeleton, forming fluorapatite, which increases the skeleton's hardness and stability. It is also anecdotally reported to enhance white and blue coloration in SPS corals.",
        recipe: {
            amounts: { '100': '0.22g', '250': '0.55g', '500': '1.10g', '1000': '2.20g', '3785': '8.33g' },
            template: `Dissolve <strong>{amount}</strong> of Sodium Fluoride (NaF) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Fluoride level in 100 L of water by approximately 0.01 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.01,
    },
    zinc: {
        kind: 'parameter',
        slug: 'zinc',
        name: 'Zinc (ZnSO4·7H2O)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A highly diluted solution of Zinc Sulfate Heptahydrate, a salt for trace zinc supplementation, requiring serial dilution for safe dosing.',
        what_it_does: "Zinc is an essential trace element that acts as a cofactor for many enzymes that regulate skeletal growth. It also plays a role in protecting corals from oxidative stress (a 'sunscreen' effect) and is necessary for the formation of certain color pigments.",
        recipe: {
            template: `<p>This is a serial dilution recipe:</p><ol class="list-decimal list-inside space-y-2">
                <li><strong>Primary Stock:</strong> Dissolve 1.0 g of Zinc Sulfate Heptahydrate (ZnSO4·7H2O) in 1 L of RO/DI water.</li>
                <li><strong>Secondary Stock:</strong> Dilute 10 mL of Primary Stock into 990 mL of RO/DI water (1:100 dilution).</li>
            </ol>`,
        },
        dosing_effect: '1 mL of the secondary stock will raise the Zinc level in 100 L of water by approximately 0.023 µg/L.',
        units: 'µg/L',
        ref_vol_L: 100,
        raise: 0.023,
    },
    boron: {
        kind: 'parameter',
        slug: 'boron',
        name: 'Boron (H3BO3)',
        category: 'Minor and Trace Elements',
        what_it_is: 'A solution of Boric Acid, a weak acid used for boron supplementation. Boron is a minor element in seawater, present at about 4.5 ppm.',
        what_it_does: 'Contributes to the seawater buffering system to help stabilize pH, particularly at the higher end of the scale. It is also involved in aragonite formation and is believed by some to enhance red and pink coloration in corals.',
        recipe: {
            amounts: { '100': '2.50g', '250': '6.25g', '500': '12.50g', '1000': '25.00g', '3785': '94.63g' },
            template: `Dissolve <strong>{amount}</strong> of Boric Acid (H3BO3) in enough RO/DI water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL will raise the Boron level in 100 L of water by approximately 0.044 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.044,
    },
    // DIY Analytical Reagents
    alk_test: {
        kind: 'info-only',
        slug: 'alk-test',
        name: 'DIY Alkalinity Titration Test',
        category: 'DIY Analytical Reagents and Standards',
        what_it_is: 'A homemade test kit that determines alkalinity by titrating a water sample with a known concentration of acid (the titrant) and an indicator solution.',
        what_it_does: "Titration allows for a very precise measurement of alkalinity (buffering capacity). The volume of acid required to overcome the sample's buffering capacity and cause a sharp pH drop (indicated by a color change) is used to calculate the dKH, providing a more accurate result than many colorimetric hobby-grade test kits.",
        recipe: {
            template: `
                <h4 class="font-bold text-slate-300">Reagent Recipes:</h4>
                <ul class="list-disc list-inside space-y-2 mt-2">
                    <li><strong>Titrant (0.1 N HCl):</strong> Created via a two-step dilution of Muriatic Acid. 1) Dilute 100 mL of ~10 N Muriatic Acid into 900 mL of RO/DI water to make a 1 N solution. 2) Dilute 100 mL of the 1 N solution into 900 mL of RO/DI water to make the final 0.1 N titrant.</li>
                    <li><strong>Indicator Solution:</strong> A blend of Bromocresol Green and Methyl Red in RO/DI water that provides a sharp color change at the titration endpoint.</li>
                </ul>`,
        },
        dosing_effect: 'Not a dosable supplement.',
    },
    alk_standard: {
        kind: 'info-only',
        slug: 'alk-standard',
        name: 'DIY Alkalinity Primary Standard',
        category: 'DIY Analytical Reagents and Standards',
        what_it_is: "A reference solution with a precisely known alkalinity value, created by dissolving a specific weight of a 'primary standard' grade chemical (anhydrous Sodium Carbonate) in a specific volume of pure water.",
        what_it_does: 'This solution is used to calibrate and verify the accuracy of both digital checkers (like a Hanna checker) and titration-based test kits. Regularly checking your testing equipment against a known standard ensures your measurements are reliable and your dosing is accurate.',
        recipe: {
            template: `<p>Precisely weigh 1.060 g of anhydrous (oven-dried) primary standard grade Sodium Carbonate (Na2CO3). Dissolve this in a 1 L Class A volumetric flask, filling to the 1 L mark with RO/DI water. This creates a 56 dKH standard.</p>`,
        },
        dosing_effect: 'Not a dosable supplement.',
    },
    salinity_standard: {
        kind: 'info-only',
        slug: 'salinity-standard',
        name: 'DIY Salinity Calibration Standard',
        category: 'DIY Analytical Reagents and Standards',
        what_it_is: 'A homemade reference solution designed specifically to calibrate optical refractometers. It is created by mixing a precise weight of pure sodium chloride with pure water.',
        what_it_does: 'This 3.65% NaCl solution has the same refractive index as 35 ppt seawater. This makes it a perfect, low-cost standard for calibrating optical refractometers, which measure salinity based on light refraction. Note: It is NOT 35 ppt seawater and is invalid for calibrating conductivity probes or hydrometers.',
        recipe: {
            template: `<p>Create a 3.65% by weight solution by dissolving 3.65 grams of pure, non-iodized Sodium Chloride (NaCl) in 96.35 grams (96.35 mL) of RO/DI water.</p>`,
        },
        dosing_effect: 'Not a dosable supplement.',
    },
};
const FRESHWATER_SOLUTIONS_BASE = {
    // Macronutrients
    n_kno3: {
        kind: 'parameter',
        slug: 'n-kno3',
        name: 'Nitrogen (from Potassium Nitrate - KNO3)',
        category: 'Macronutrient Formulation and Dosing',
        what_it_is: 'Potassium Nitrate, a chemical salt that efficiently supplies two essential macronutrients—nitrogen (as nitrate) and potassium—to aquatic plants.',
        what_it_does: 'Nitrogen is a core component of chlorophyll (for photosynthesis) and amino acids (the building blocks of proteins), making it essential for vigorous growth and vibrant green foliage. As a mobile nutrient, deficiency symptoms like yellowing (chlorosis) typically appear on older, lower leaves first as the plant moves nitrogen to new growth.',
        recipe: {
            amounts: { '100': '12.20g', '250': '30.50g', '500': '61.00g', '1000': '122.00g', '3785': '461.77g' },
            template: `Dissolve <strong>{amount}</strong> of dry Potassium Nitrate (KNO3) powder into enough distilled or reverse osmosis (RO) water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Nitrate (NO3) level in 100 L of water by approximately 0.75 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.75,
    },
    p_kh2po4: {
        kind: 'parameter',
        slug: 'p-kh2po4',
        name: 'Phosphorus (from Monopotassium Phosphate - KH2PO4)',
        category: 'Macronutrient Formulation and Dosing',
        what_it_is: 'Monopotassium Phosphate, a highly soluble salt that provides both phosphorus (as phosphate) and potassium to plants.',
        what_it_does: 'Phosphorus is central to all life, forming the backbone of energy transfer molecules (ATP), DNA, RNA, and cell membranes. It is vital for all metabolic processes, photosynthesis, cell division, and healthy root development. Deficiencies can manifest as stunted growth or a dark, purplish tint to leaves.',
        recipe: {
            amounts: { '100': '1.60g', '250': '4.00g', '500': '8.00g', '1000': '16.00g', '3785': '60.56g' },
            template: `Dissolve <strong>{amount}</strong> of dry Monopotassium Phosphate (KH2PO4) powder into enough distilled or RO water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Phosphate (PO4) level in 100 L of water by approximately 0.11 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.11,
    },
    k_k2so4: {
        kind: 'parameter',
        slug: 'k-k2so4',
        name: 'Potassium (from Potassium Sulfate - K2SO4)',
        category: 'Macronutrient Formulation and Dosing',
        what_it_is: 'Potassium Sulfate, a salt used to supply potassium without adding nitrogen or phosphorus. This allows for independent control of potassium levels.',
        what_it_does: 'Potassium is a crucial macronutrient that activates dozens of enzymes, regulates the movement of water and nutrients within the plant, and controls the opening and closing of stomata for gas exchange (CO2 uptake). It is dosed separately to supplement the potassium already added by KNO3 and KH2PO4, ensuring a balanced nutrient ratio and preventing deficiencies, which often appear as pinholes in older leaves.',
        recipe: {
            amounts: { '100': '11.00g', '250': '27.50g', '500': '55.00g', '1000': '110.00g', '3785': '416.35g' },
            template: `Dissolve <strong>{amount}</strong> of dry Potassium Sulfate (K2SO4) powder into enough distilled or RO water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Potassium (K) level in 100 L of water by approximately 0.49 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.49,
    },
    // Water Hardness
    ca_cacl2: {
        kind: 'parameter',
        slug: 'ca-cacl2',
        name: 'Calcium (from Calcium Chloride Dihydrate - CaCl2·2H2O)',
        category: 'Managing Water Hardness and Secondary Nutrients',
        what_it_is: 'Calcium Chloride Dihydrate, a hygroscopic salt used to raise General Hardness (GH) by adding calcium ions (Ca2+). The dissolution process can be exothermic, causing the water to warm up.',
        what_it_does: 'Calcium is a secondary macronutrient essential for the structural integrity of plant cell walls and membranes. As an immobile nutrient, it cannot be moved from old to new tissue, so deficiency symptoms like stunted, twisted, or deformed new growth appear on the youngest leaves and shoots.',
        recipe: {
            amounts: { '100': '5.00g', '250': '12.50g', '500': '25.00g', '1000': '50.00g', '3785': '189.25g' },
            template: `Dissolve <strong>{amount}</strong> of Calcium Chloride Dihydrate (CaCl2·2H2O) into enough distilled or RO water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Calcium (Ca) level in 100 L of water by approximately 0.14 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.14,
    },
    mg_mgso4: {
        kind: 'parameter',
        slug: 'mg-mgso4',
        name: 'Magnesium (from Magnesium Sulfate Heptahydrate - MgSO4·7H2O)',
        category: 'Managing Water Hardness and Secondary Nutrients',
        what_it_is: 'Magnesium Sulfate Heptahydrate, commonly known as Epsom salt. It is used to raise General Hardness (GH) by adding magnesium ions (Mg2+).',
        what_it_does: 'Magnesium is the central atom in the chlorophyll molecule, making it absolutely indispensable for capturing light energy for photosynthesis. It also plays a role in enzyme activation. It is crucial to maintain an optimal Ca:Mg ratio (typically between 3:1 and 4:1) to ensure neither element interferes with the absorption of the other.',
        recipe: {
            amounts: { '100': '14.00g', '250': '35.00g', '500': '70.00g', '1000': '140.00g', '3785': '529.90g' },
            template: `Dissolve <strong>{amount}</strong> of Magnesium Sulfate Heptahydrate (MgSO4·7H2O) into enough distilled or RO water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Magnesium (Mg) level in 100 L of water by approximately 0.14 ppm.',
        units: 'ppm',
        ref_vol_L: 100,
        raise: 0.14,
    },
    alk_khco3: {
        kind: 'parameter',
        slug: 'alk-khco3',
        name: 'Alkalinity (from Potassium Bicarbonate - KHCO3)',
        category: 'Managing Water Hardness and Secondary Nutrients',
        what_it_is: 'Potassium Bicarbonate, a chemical salt used to raise Carbonate Hardness (KH) while also adding some potassium.',
        what_it_does: "Carbonate Hardness (KH) is a measure of the water's buffering capacity. It serves as the aquarium's defense against rapid pH changes by neutralizing acids. Maintaining a stable KH is critical for a stable environment, especially when injecting CO2, as the carbonic acid produced will lower the pH. A sufficient KH level prevents a dangerous pH crash.",
        recipe: {
            amounts: { '100': '10.00g', '250': '25.00g', '500': '50.00g', '1000': '100.00g', '3785': '378.50g' },
            template: `Dissolve <strong>{amount}</strong> of Potassium Bicarbonate (KHCO3) into enough distilled or RO water to make a final volume of {volume}.`,
        },
        dosing_effect: '1 mL of this solution will raise the Carbonate Hardness in 100 L of water by approximately 0.028 dKH.',
        units: 'dKH',
        ref_vol_L: 100,
        raise: 0.028,
    },
    // Micronutrients
    trace_mix: {
        kind: 'info-only',
        slug: 'trace-mix',
        name: 'Consolidated Trace Mix (Recommended Method)',
        category: 'Micronutrient (Trace) Solutions',
        what_it_is: 'A single, comprehensive solution containing a balanced ratio of six essential micronutrients (or trace elements). This solution must be stored separately from any phosphate solutions to prevent the iron from precipitating out and becoming unusable by plants.',
        what_it_does: 'Provides a complete and reliable method for dosing all essential micronutrients, which are vital for various enzymatic and metabolic functions but are needed in very small quantities. Dosing them individually is impractical and highly prone to error due to the extremely small weights of dry salts required. A consolidated mix ensures plants get everything they need in the correct proportions.',
        recipe: {
            template: `<p>To create a 1 liter (1000 mL) solution, dissolve the following dry salts into RO water, dissolving each completely before adding the next:</p>
            <ul class="list-disc list-inside space-y-1 mt-2">
                <li><strong>15.00 g</strong> Ferrous Gluconate (C12H22FeO14)</li>
                <li><strong>3.50 g</strong> Manganese Chloride Tetrahydrate (MnCl2·4H2O)</li>
                <li><strong>2.00 g</strong> Boric Acid (H3BO3)</li>
                <li><strong>0.40 g</strong> Zinc Sulfate Heptahydrate (ZnSO4·7H2O)</li>
                <li><strong>0.15 g</strong> Sodium Molybdate Dihydrate (Na2MoO4·2H2O)</li>
                <li><strong>0.08 g</strong> Copper (II) Sulfate Pentahydrate (CuSO4·5H2O)</li>
            </ul>`,
        },
        dosing_effect: '1 mL of this solution will raise the micronutrient levels in 100 L of water by the following amounts: Fe: 0.0188 ppm, Mn: 0.0097 ppm, B: 0.0035 ppm, Zn: 0.0009 ppm, Mo: 0.0006 ppm, Cu: 0.0002 ppm.',
        units: 'mL',
    },
    // CO2
    co2: {
        kind: 'info-only',
        slug: 'co2',
        name: 'Mastering Carbon Dioxide (CO2) Injection',
        category: 'Carbon Dioxide (CO2) Injection',
        what_it_is: 'Pressurized carbon dioxide gas that is injected and dissolved into aquarium water. This forms carbonic acid (H2CO3), which provides a readily available source of carbon for aquatic plants.',
        what_it_does: "In a high-light, high-nutrient aquarium, CO2 is the single most important nutrient and the primary driver of plant growth. Carbon makes up about 45% of a plant's dry mass. Providing non-limiting levels of dissolved CO2 (typically targeting 30 ppm) prevents carbon from being the bottleneck for growth, allowing plants to photosynthesize efficiently, grow lushly, and outcompete algae.",
        recipe: {
            template: `
            <h4 class="font-bold text-slate-300">Dosing Logic and Automation:</h4>
            <p>The concentration of dissolved CO2 can be accurately estimated by measuring the pH and Carbonate Hardness (KH) of the water. This relationship is described by the well-known formula:</p>
            <p class="font-mono text-cyan-300 text-center my-4 text-lg">CO2(ppm) = 3 × KH(dKH) × 10^(7-pH)</p>
            <p>This formula is the engine behind pH controllers, which automate CO2 injection. To use one, you first establish a stable KH, then use the formula to calculate the target pH needed to achieve your desired CO2 level. The controller then switches the CO2 solenoid on or off to maintain that pH.</p>
            <h4 class="font-bold text-slate-300 mt-4">Example:</h4>
            <p>To achieve a target CO2 of <strong>30 ppm</strong> in a tank with a stable KH of <strong>4 dKH</strong>, the target pH would be calculated as <strong>6.6</strong>. A pH controller would be set to this pH to turn the CO2 on and off, maintaining the target concentration during the lighting period.</p>
            `,
        },
        dosing_effect: 'This is a concept, not a chemical solution with a direct dosing effect.',
    },
};
/**
 * Merge verbatim reference info (`RECIPE_INFO`) onto a base catalog. The base
 * entry's fields always win, so the verified `raise`/`recipe` data and any
 * existing `safety`/`notes` are preserved; the reference only fills the new
 * `ingredients`/`equipment`/`storage`/`usageTips`/`safety` detail sections.
 */
function withRecipeInfo(cat) {
    const out = {};
    for (const [key, sol] of Object.entries(cat)) {
        const info = RECIPE_INFO[sol.name];
        if (!info) {
            out[key] = sol;
            continue;
        }
        switch (sol.kind) {
            case 'parameter':
                out[key] = { ...info, ...sol };
                break;
            case 'ratio':
                out[key] = { ...info, ...sol };
                break;
            case 'info-only':
                out[key] = { ...info, ...sol };
                break;
        }
    }
    return out;
}
export const REEF_SOLUTIONS = withRecipeInfo(REEF_SOLUTIONS_BASE);
export const FRESHWATER_SOLUTIONS = withRecipeInfo(FRESHWATER_SOLUTIONS_BASE);
//# sourceMappingURL=catalog.js.map